import { Avatar, Box, Flex, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { useActiveListId } from 'hooks/useActiveListId';
import { ISidebarPitchList } from 'interfaces/i-sidebar';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import useListLibraryStore from 'stores/use-list-library-store';

interface IProps {
  file: ISidebarPitchList;
}

export const PitchList = (props: IProps) => {
  const navigate = useNavigate();

  const activeListId = useActiveListId();

  const loading = useListLibraryStore((s) => s.loading);

  const safePath = useMemo(
    () => [props.file.pathEnd, props.file.name].filter((s) => !!s).join('/'),
    [props.file]
  );

  const isActiveList = activeListId === props.file.object._id;

  const abbr = useMemo(() => {
    const safeChars = StringHelper.getInitials(props.file.name);

    if (safeChars.length === 0) {
      // fallback for unnamed list
      return '*';
    }

    // only take the first letter of the first word
    return safeChars.substring(0, 1);
  }, [props.file.name]);

  const className = useMemo(
    () =>
      StringHelper.classNames([
        'Entry',
        loading ? 'cursor-not-allowed' : 'cursor-pointer',
      ]),
    [loading]
  );

  return (
    <ErrorBoundary identifier="PitchListItem">
      <Flex
        className={className}
        gap="2"
        p="2"
        mr="2"
        align="center"
        onClick={(e) => {
          // prevents click from collapsing parent folder
          e.stopPropagation();

          if (loading) {
            return;
          }

          if (isActiveList) {
            return;
          }

          navigate(paths.pitches.list(props.file.object._id));
        }}
      >
        <Box>
          <Avatar
            size="1"
            fallback={abbr}
            color={isActiveList ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL}
            style={{
              width: '20px',
              height: '20px',
            }}
          />
        </Box>
        <Box
          // required for nested text truncation to work
          minWidth="0"
          flexGrow="1"
          title={safePath}
          data-testid="SidebarPitchList"
        >
          <Text
            color={isActiveList ? RADIX.COLOR.ACCENT : undefined}
            // as div + overflow hidden allows truncate to take effect
            as="div"
            truncate
          >
            {props.file.name}
          </Text>
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
